import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import { translateManyClient } from "../../../utils/ItClient";
import i18next from "i18next";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/reservations`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken(); // Get token from cookies or storage
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", "true");
    }
    return headers;
  },
});
// SS
export const ReservationSlice = createApi({
  reducerPath: "ReservationSlice",
  baseQuery: baseQuery,
  tagTypes: ["reservations"],
  endpoints: (builder) => ({
    // GET all reservations with filters
    getAllReservations: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search,
        endPoint,
        status,
        booking_type,
        charter_frequency,
        charter_type,
        subscription_type,
        role,
      }) => {
        const params = new URLSearchParams();

        params.append("page", page);
        params.append("limit", limit);

        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (role) params.append("role", role);
        if (booking_type) params.append("booking_type", booking_type);
        if (charter_frequency)
          params.append("charter_frequency", charter_frequency);
        if (charter_type) params.append("charter_type", charter_type);
        if (subscription_type)
          params.append("subscription_type", subscription_type);

        return `${endPoint}?${params.toString()}`;
      },

      // / dynamic localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18next.language || "en");
          if (lang === "en") return;

          const vehiclesData = data?.data?.reservations ?? [];
          if (!Array.isArray(vehiclesData) || vehiclesData.length === 0) return;

          // Collect strings to translate (skip PII like emails)
          const bucket = [];
          for (const vehicle of vehiclesData) {
            if (vehicle.school_company_name)
              bucket.push(vehicle.school_company_name);
            if (vehicle.school_full_name) bucket.push(vehicle.school_full_name);
            if (vehicle.driver_name) bucket.push(vehicle.driver_name);
            if (vehicle.booking_type) bucket.push(vehicle.booking_type);
            if (vehicle.reservation_type) bucket.push(vehicle.reservation_type);
            if (vehicle.vehicle_type_name)
              bucket.push(vehicle.vehicle_type_name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            ReservationSlice.util.updateQueryData(
              "getAllReservations",
              arg,
              (draft) => {
                const arr = draft?.data?.reservations ?? [];
                arr.forEach((vehicle) => {
                  vehicle.school_company_name = pick(
                    vehicle.school_company_name,
                  );
                  vehicle.school_full_name = pick(vehicle.school_full_name);
                  vehicle.driver_name = pick(vehicle.driver_name);
                  vehicle.booking_type = pick(vehicle.booking_type);
                  vehicle.reservation_type = pick(vehicle.reservation_type);
                  vehicle.vehicle_type_name = pick(vehicle.vehicle_type_name);
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["reservations"],
    }),
    // GET vehicle by vendor ID
    getVendorDetails: builder.query({
      query: (id) => `/${id}`,
      providesTags: ["Vendor"],
    }),
    // school make reservation
    useCreateReservationMutation: builder.mutation({
      query: (data) => ({
        url: "/busing-subscription",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["reservations"],
    }),
    // school make reservation
    createBusingSubscription: builder.mutation({
      query: (data) => ({
        url: "/busing-subscription",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["reservations"],
    }),

    // Update reservation
    updateBusingSubscription: builder.mutation({
      query: ({ data, id }) => ({
        url: `/busing-subscription/${id}`,
        method: "Put",
        body: data,
      }),
      invalidatesTags: ["reservations"],
    }),

    // Create One Time || Recurring Bus Charter Reservation (For School User)
    createBusCharter: builder.mutation({
      query: (data) => ({
        url: "bus-charter",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["reservations"],
    }),

    // vendor assing reservation to Driver
    assignReservation: builder.mutation({
      query: ({ data, id, endPoint }) => ({
        url: `/${id}/${endPoint}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["reservations", "Vendor"],
    }),

    // Unassign driver from reservation
    unassignReservation: builder.mutation({
      query: (id) => ({
        url: `/${id}/unassign`,
        method: "PATCH",
      }),
      invalidatesTags: ["reservations", "Vendor"],
    }),

    // Switch vehicle/driver on an already-scheduled reservation, in place —
    // no cancellation, parents only get an "assignment updated" notice.
    // data: any subset of { driver_id, vehicle_id, route_id }.
    switchVehicleDriver: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/switch`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["reservations", "Vendor"],
    }),

    // Admin: Cancel all active reservations for a parent
    cancelParentReservations: builder.mutation({
      query: (parentId) => ({
        url: `/parent/${parentId}/cancel-all`,
        method: "PATCH",
      }),
    }),
    // Update reservation
    updateReservation: builder.mutation({
      query: ({ data, id }) => ({
        url: `bus-charter/${id}`,
        method: "Put",
        body: data,
      }),
      invalidatesTags: ["reservations"],
    }),
    // DELETE a reservation
    deleteReservation: builder.mutation({
      query: ({ endPoint, id }) => ({
        url: `${endPoint}/${id}`,
        method: "Delete",
      }),
      invalidatesTags: ["reservations"],
    }),

    cancelReservation: builder.mutation({
      query: ({ endPoint, reservationId, payload }) => ({
        url: `${endPoint}/${reservationId}/cancel`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["reservations"],
    }),

    getAllDates: builder.query({
      query: (reservationId) => ({
        url: `/all-dates/${reservationId}`,
        method: "GET",
      }),
    }),

    getReservationStudents: builder.query({
      query: ({ id, page = 1, limit = 50 }) =>
        `/${id}/students?page=${page}&limit=${limit}`,
      providesTags: ["reservations"],
    }),

    getReservationDetails: builder.query({
      query: (id) => `/${id}/details`,
      providesTags: ["reservations"],
    }),

    uploadStudentList: builder.mutation({
      query: ({ id, file, source }) => {
        const formData = new FormData();
        formData.append("student_list_file", file);
        const url = `/busing-subscription/${id}/upload-students${source ? `?source=${source}` : ""}`;
        return {
          url,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["reservations"],
    }),

    // Remove a student from a reservation
    removeStudentFromReservation: builder.mutation({
      query: ({ reservationId, studentId }) => ({
        url: `/${reservationId}/students/${studentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["reservations"],
    }),
  }),
});
// Export hooks for usage in components
export const {
  useGetAllReservationsQuery, // old use
  useCreateBusCharterMutation, // new
  useDeleteReservationMutation, //old use
  useCancelReservationMutation, //new
  useGetAllDatesQuery, //new
  useCreateBusingSubscriptionMutation, // modidy
  useUpdateReservationMutation,
  useAssignReservationMutation,
  useUnassignReservationMutation, //new
  useSwitchVehicleDriverMutation,
  useCancelParentReservationsMutation, //new
  useUpdateBusingSubscriptionMutation, // modify
  useCreateReservationMutation,
  useGetReservationStudentsQuery,
  useGetReservationDetailsQuery,
  useUploadStudentListMutation,
  useRemoveStudentFromReservationMutation,
} = ReservationSlice;
